<template>
  <div class="feedback-controls">
    <BittsButton
      :loading="feedbackLoading"
      :disabled="Boolean(feedbackAlreadyGiven(message.messageId))"
      :center-icon="['fak', 'thumbs-up']"
      aria-label="Helpful"
      size="x-small"
      variant="outline"
      :center-icon-classes="
        getCenterIconClasses(message.messageId || '', 'like')
      "
      class="feedback-controls_feedback-button"
      @click="() => giveFeedback(true, message.messageId)"
    />
    <BittsDropdown
      :mount-to-body="true"
      trigger="click"
      placement="top"
      :options="OPTIONS"
      parent-class="feedback-controls__feedback-dropdown"
      @selection="giveNegativeFeedback"
    >
      <template #prompt="{ triggerProps }">
        <BittsButton
          v-bind="triggerProps"
          :loading="feedbackLoading"
          :disabled="Boolean(feedbackAlreadyGiven(message.messageId))"
          :center-icon="['fak', 'thumbs-down']"
          aria-label="Not helpful"
          size="x-small"
          variant="outline"
          class="feedback-controls_feedback-button"
          :center-icon-classes="
            getCenterIconClasses(message.messageId || '', 'dislike')
          "
        />
      </template>
    </BittsDropdown>
    <BittsPopover
      trigger="click"
      :autoclose="1000"
      overlay-class="input-popover"
      placement="top"
    >
      <BittsButton
        :center-icon="['fak', 'copy']"
        aria-label="Copy message"
        @click="onCopyMsg(message.content)"
        size="x-small"
        type="neutral"
        variant="outline"
      />
      <template #content>
        <span class="text-white"> Copied to clipboard </span>
      </template>
    </BittsPopover>
    <BittsModal
      v-if="reportIssueModalVisible"
      :loading="feedbackLoading"
      :width="560"
      :disabled="!customFeedback"
      title="Report an issue"
      description="If you think this message is inappropriate or violates our community guidelines, please let us know."
      :primary-button-text="'Submit'"
      @action="giveFeedback(false, message.messageId, customFeedback)"
      @closed="() => (reportIssueModalVisible = false)"
    >
      <BittsInput
        v-model="customFeedback"
        name="report-issue"
        placeholder="Describe the issue"
      />
    </BittsModal>
  </div>
</template>
<script setup lang="ts">
import {
  BittsButton,
  BittsDropdown,
  BittsInput,
  BittsModal,
  BittsPopover,
} from '@crossbeam/bitts';
import { Nullable } from '@crossbeam/types';

import { ref } from 'vue';

import { agentApi } from '@/api';
import {
  MessageFeedback,
  ReceivedMessage,
} from '@/composables/mcp/useAIChatState';
import useAuth from '@/composables/useAuth';
import useIteratively from '@/composables/useIteratively';
import { useFlashesStore } from '@/stores';
import { copyToClipBoard } from '@/utils';

const { message, baseItlyBody, chatSessionId, messagesWithFeedback } =
  defineProps<{
    message: ReceivedMessage;
    baseItlyBody: {
      event_site: string;
      conversation_id: string;
    };
    chatSessionId: Nullable<string>;
    messagesWithFeedback: MessageFeedback[];
  }>();

const emit =
  defineEmits<(e: 'feedback-added', payload: MessageFeedback) => void>();

const { iteratively } = useIteratively();

const OPTIONS = [
  { value: 'Wrong answer', display: 'Wrong answer' },
  { value: 'Off-topic', display: 'Off-topic' },
  { value: 'Link doesn’t work', display: 'Link doesn’t work' },
  {
    value: 'Missing context',
    display: 'Missing context',
  },
  { value: 'Overactive refusal', display: 'Overactive refusal' },
  {
    value: 'Poorly formatted',
    display: 'Poorly formatted',
  },
  { value: 'Other', display: 'Other' },
  { value: 'Report bug', display: 'Report bug' },
];

const flashesStore = useFlashesStore();
const feedbackLoading = ref(false);
const { currentOrg } = useAuth();
function getCenterIconClasses(id: string, feedback: 'like' | 'dislike') {
  const feedbackGiven = feedbackAlreadyGiven(id);
  if (!feedbackGiven || feedbackGiven.feedback !== feedback) {
    return [];
  }
  return ['text-secondary-text'];
}
function feedbackAlreadyGiven(id = '') {
  return messagesWithFeedback.find((msg) => msg.id === id);
}
async function giveFeedback(feedback: boolean, id = '', option = '') {
  feedbackLoading.value = true;
  const body: {
    chat_session_id: string;
    message_id: string;
    feedback: boolean;
    feedback_reason?: string;
  } = {
    chat_session_id: chatSessionId as string,
    message_id: id,
    feedback,
  };
  if (option) {
    body.feedback_reason = option;
  }
  const { error } = await agentApi.POST('/feedback', {
    body,
    params: {
      query: {
        organization_uuid: currentOrg.value?.uuid,
      },
    },
  });

  emit('feedback-added', {
    id,
    feedback: feedback ? 'like' : 'dislike',
  });

  if (error) {
    feedbackLoading.value = false;
    flashesStore.addErrorFlash({
      message: 'Error giving feedback',
      description: 'Please try again later.',
    });
    return;
  }
  flashesStore.addSuccessFlash({
    message: 'Thank you for your feedback!',
    description: 'Your feedback helps us improve Crossbeam AI.',
  });
  feedbackLoading.value = false;
  reportIssueModalVisible.value = false;
}

const reportIssueModalVisible = ref(false);
const customFeedback = ref('');
function giveNegativeFeedback({ value }: { value: string }) {
  if (value === 'Report bug') {
    reportIssueModalVisible.value = true;
    return;
  }
  giveFeedback(false, message.messageId, value);
}

function onCopyMsg(message: string) {
  const doc = new DOMParser().parseFromString(message, 'text/html');
  const text = doc.body.textContent || '';

  copyToClipBoard(text);
  iteratively.aiChatAction({
    ...baseItlyBody,
    type: 'copy_message',
    metadata: {
      message: text,
    },
  });
}
</script>

<style lang="pcss">
@reference "../../css/theme.css";
.feedback-controls {
  @apply text-xs text-neutral-text-weak flex items-center gap-4 my-12;
  span.c-bitts-input__input.ant-input-affix-wrapper:not(
      .ant-input-affix-wrapper-disabled,
      .disabled,
      .danger,
      .success
    ) {
    @apply outline-hidden;
  }
}
.feedback-controls__feedback-dropdown {
  .c-bitts-dropdown-selections__section-separator {
    @apply hidden;
  }
}
</style>
